<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed } from "vue";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import FeatureCard from "@/components/FeatureCard.vue";
import IconDyslexicFont from "@/components/icons/white/IconWhiteDyslexicFont.vue";
import IconLaptopScreenReader from "@/components/icons/white/IconWhiteLaptopScreenReader.vue";
import IconMic from "@/components/icons/white/IconWhiteMic.vue";
import ImageResponsive from "@/components/ImageResponsive.vue";
import VideoResponsive from "@/components/VideoResponsive.vue";
import { useAuthStore, useSettingsStore } from "@/stores";
import type { PackageGroupId } from "@/types/site";

const { isAuthenticated } = storeToRefs(useAuthStore());
const { preferredPackageGroup } = storeToRefs(useSettingsStore());

const packageGroup = computed<PackageGroupId>(() => preferredPackageGroup.value?.id ?? "ordbogen");

const ctaButtonQuery = computed(() => {
  if (isAuthenticated.value) {
    return {};
  }

  return { directlyToUnilogin: true };
});
</script>

<template>
  <main class="about">
    <section class="about__section about__section-welcome">
      <div class="container">
        <div class="row">
          <div class="col-12 col-md-7 col-lg-6 mt-md-4">
            <h1 class="about__headline about__headline--secondary text-md-left mb-4">
              {{ $t("about.headlines.welcome") }}
            </h1>
            <p class="about__lead mb-4 mb-md-5 text-center text-md-left">
              {{ $t("about.texts.lead") }}
            </p>
            <div class="row no-gutters">
              <div
                class="col-12 col-lg-initial mb-4 mb-xl-0 d-flex d-md-inline-block justify-content-center"
              >
                <TgButton
                  tag="router-link"
                  size="lg"
                  :to="{ name: 'login', query: ctaButtonQuery }"
                >
                  {{ $t("about.ctaBtnText") }}
                </TgButton>
              </div>
              <div
                class="col-12 col-lg-initial ml-xl-4 d-flex d-md-inline-block justify-content-center"
              >
                <TgButton
                  tag="router-link"
                  variant="outline"
                  size="lg"
                  :to="{ name: 'get-access-today' }"
                >
                  {{ $t("about.ctaBtnGetAccessText") }}
                </TgButton>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-5 col-lg-6 align-self-center mt-5 mt-md-0">
            <ImageResponsive
              src="/assets/img/about/children-with-devices@1x.png"
              width="750"
              height="551"
              img-class="image-contain"
              presentational
            />
          </div>
          <div class="col-12 row no-gutters about__publishers">
            <div class="col-12 col-md-3 d-flex align-items-center">
              <p class="about__publishers-text">{{ $t("about.publishersText") }}</p>
            </div>
            <div class="col-12 col-md-9">
              <div class="row d-flex align-items-center">
                <div class="col-6 col-md-3 mt-3 mt-md-0 text-center text-md-left">
                  <ImageResponsive
                    src="/assets/img/about/publishers/gyldendal@1x.png"
                    width="167"
                    height="36"
                    :alt="$t('about.imageAlts.publishers.gyldendal')"
                  />
                </div>
                <div class="col-6 col-md-3 mt-3 mt-md-0 text-center">
                  <ImageResponsive
                    src="/assets/img/about/publishers/dansk-sprognaevn@1x.png"
                    width="118"
                    height="43"
                    :alt="$t('about.imageAlts.publishers.dsn')"
                  />
                </div>
                <div class="col-6 col-md-3 mt-3 mt-md-0 text-center text-md-left">
                  <ImageResponsive
                    src="/assets/img/about/publishers/politikens-forlag@1x.png"
                    width="144"
                    height="31"
                    :alt="$t('about.imageAlts.publishers.politiken')"
                  />
                </div>
                <div class="col-6 col-md-3 mt-3 mt-md-0 text-center text-md-right">
                  <ImageResponsive
                    src="/assets/img/about/publishers/ordbogen@1x.png"
                    width="199"
                    height="28"
                    :alt="$t('about.imageAlts.publishers.ordbogen')"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="about__section">
      <div class="container">
        <div class="row mb-5">
          <div class="col-12 text-center">
            <h2 class="about__headline about__headline--secondary">
              {{ $t("about.headlines.studentFriendly") }}
            </h2>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-6 d-flex flex-column mb-5 mb-md-0">
            <div class="w-100 px-2 d-flex justify-content-center">
              <div class="about__background-box mb-5">
                <ImageResponsive
                  lazy
                  width="448"
                  height="234"
                  src="/assets/img/about/dk-betegnelser@1x.png"
                  class="about__wordclass-picture"
                  presentational
                />
              </div>
            </div>
            <div class="row justify-content-center text-center">
              <h3 class="col-12 about__subheading about__subheading--primary mb-3">
                {{ $t("about.subheadings.danishTerms") }}
              </h3>
              <p class="about__lead col-12 col-md-9">
                {{ $t("about.texts.danishTerms") }}
              </p>
            </div>
          </div>
          <div class="col-12 col-md-6 d-flex flex-column">
            <div class="w-100 px-2 d-flex justify-content-center">
              <div class="about__background-box mb-4 mb-md-5">
                <ImageResponsive
                  lazy
                  width="452"
                  height="255"
                  src="/assets/img/about/select-lang@1x.png"
                  class="about__language-groups-picture"
                  presentational
                />
              </div>
            </div>
            <div class="row justify-content-center text-center">
              <h3 class="col-12 about__subheading about__subheading--primary mb-3">
                {{ $t("about.subheadings.easyLanguageSelection") }}
              </h3>
              <p class="about__lead col-12 col-md-9">
                {{ $t("about.texts.easyLanguageSelection") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="about__section about__section-accessibility">
      <div class="container">
        <div class="row mb-5">
          <div class="col-12 text-center">
            <h2 class="about__headline about__headline--secondary">
              {{ $t("about.headlines.accessibilityForAll") }}
            </h2>
          </div>
        </div>
        <div class="row mb-md-5 text-center">
          <div class="col-12 col-lg-4">
            <div class="about__accessibility-usp-box">
              <div class="about__accessibility-icon">
                <IconMic width="22px" height="32px" role="presentation" />
              </div>
              <h3 class="col-12 about__subheading about__subheading--secondary mb-3">
                {{ $t("about.subheadings.speechToText") }}
              </h3>
              <p class="about__lead">
                {{ $t("about.texts.speechToText") }}
              </p>
            </div>
          </div>
          <div class="col-12 col-lg-4">
            <div class="about__accessibility-usp-box">
              <div class="about__accessibility-icon">
                <IconDyslexicFont role="presentation" />
              </div>
              <h3 class="col-12 about__subheading about__subheading--secondary mb-3">
                {{ $t("about.subheadings.dyslexicFont") }}
              </h3>
              <i18n-t keypath="about.texts.dyslexicFont" tag="p" class="about__lead">
                <a href="https://opendyslexic.org/" target="_blank">OpenDyslexic</a>
              </i18n-t>
            </div>
          </div>
          <div class="col-12 col-lg-4">
            <div class="about__accessibility-usp-box">
              <div class="about__accessibility-icon">
                <IconLaptopScreenReader role="presentation" />
              </div>
              <h3 class="col-12 about__subheading about__subheading--secondary mb-3">
                {{ $t("about.subheadings.screenReaderOptimized") }}
              </h3>
              <p class="about__lead">
                {{ $t("about.texts.screenReaderOptimized") }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-12 d-flex justify-content-center">
          <div class="about__children-with-phone">
            <ImageResponsive
              lazy
              width="356"
              height="334"
              src="/assets/img/about/children-talking-with-phone@1x.png"
              presentational
            />
          </div>
        </div>

        <div class="row mb-4 mb-md-0 py-md-5">
          <div
            class="col-12 col-md-6 mb-4 mb-md-0 d-flex justify-content-center justify-content-md-end"
          >
            <TgButton tag="router-link" size="lg" :to="{ name: 'login', query: ctaButtonQuery }">
              {{ $t("about.ctaBtnText") }}
            </TgButton>
          </div>
          <div class="col-12 col-md-6 d-flex justify-content-center justify-content-md-start">
            <TgButton
              tag="router-link"
              variant="outline"
              size="lg"
              :to="{ name: 'get-access-today' }"
            >
              {{ $t("about.ctaBtnGetAccessText") }}
            </TgButton>
          </div>
        </div>
      </div>
    </section>
    <section class="about__section">
      <div class="container">
        <div class="row my-5 text-center">
          <div class="col-12">
            <h2 class="about__headline about__headline--secondary">
              {{ $t("about.headlines.contentYouKnow") }}
            </h2>
          </div>
          <div class="col-12 my-4">
            <p class="about__lead">{{ $t("about.texts.contentYouKnow") }}</p>
          </div>
          <div class="col-12 my-4 d-flex justify-content-center">
            <ImageResponsive
              src="/assets/img/about/children-pointing-at-dictionaries@1x.png"
              width="1123"
              height="446"
              presentational
            />
          </div>
          <div class="row col-12 mt-5">
            <div
              class="col-12 col-md-6 mb-4 mb-md-0 d-flex justify-content-center justify-content-md-end"
            >
              <TgButton tag="router-link" size="lg" :to="{ name: 'login', query: ctaButtonQuery }">
                {{ $t("about.ctaBtnText") }}
              </TgButton>
            </div>
            <div class="col-12 col-md-6 d-flex justify-content-center justify-content-md-start">
              <TgButton
                tag="router-link"
                variant="outline"
                size="lg"
                :to="{ name: 'get-access-today' }"
              >
                {{ $t("about.ctaBtnGetAccessText") }}
              </TgButton>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="about__section">
      <div class="container position-relative mb-5">
        <div class="row mb-5">
          <div class="col-12 text-center">
            <h2 class="about__headline about__headline--secondary">
              {{ $t("about.headlines.accessibilityFocus") }}
            </h2>
          </div>
        </div>
        <div class="row text-center text-md-left">
          <div class="col-12 col-lg-7 about__desktop-environment">
            <VideoResponsive
              src="/assets/img/about/tilgaengelighed-web@1x.mp4"
              width="823"
              height="606"
              class="about__accessibility-animation"
            >
              <template #reducedMotion>
                <ImageResponsive
                  src="/assets/img/about/accessibility-colorblind@1x.png"
                  width="823"
                  height="606"
                  presentational
                />
              </template>
            </VideoResponsive>
          </div>
          <div class="col-12 col-lg-5 about__accessibility-focus-text-container">
            <h3 class="about__subheading about__subheading--primary mb-3">
              {{ $t("about.subheadings.readingLines") }}
            </h3>
            <p class="about__lead mb-5">
              {{ $t("about.texts.readingLines") }}
            </p>
            <h3 class="about__subheading about__subheading--primary mb-3">
              {{ $t("about.subheadings.colorSchemes") }}
            </h3>
            <p class="about__lead mb-5">
              {{ $t("about.texts.colorSchemes") }}
            </p>
            <div class="d-flex flex-wrap justify-content-center justify-content-lg-start gap-4">
              <TgButton tag="router-link" size="lg" :to="{ name: 'login', query: ctaButtonQuery }">
                {{ $t("about.ctaBtnText") }}
              </TgButton>
              <TgButton
                class="ml-md-4 ml-lg-0"
                tag="router-link"
                variant="outline"
                size="lg"
                :to="{ name: 'get-access-today' }"
              >
                {{ $t("about.ctaBtnGetAccessText") }}
              </TgButton>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="about__section about__section-workshop container-fluid">
      <div class="about__section-workshop-info-container row px-3">
        <div class="col-lg-3 col-xl-4 pr-xl-5 d-flex justify-content-end">
          <ImageResponsive
            src="/assets/img/about/boy-with-screwdriver@1x.png"
            width="228"
            height="351"
            class="d-none d-lg-inline-block"
            presentational
          />
        </div>
        <div class="col-12 col-md-6 col-xl-4 justify-content-end about__section-workshop-info">
          <h4 class="about__small about__small--primary">
            {{ $t("about.headlines.workshop") }}
          </h4>
          <h2 class="about__headline about__headline--secondary about__section-workshop-headline">
            {{ $t("about.headlines.moreThanADictionary") }}
          </h2>
          <p class="about__lead">
            {{ $t("about.texts.workshop") }}
          </p>
        </div>
        <div
          class="col-12 col-md-5 col-lg-3 col-xl-4 mt-4 mt-sm-0 mt-lg-5 pl-xl-5 d-flex align-items-center justify-content-center justify-content-lg-start"
        >
          <ImageResponsive
            src="/assets/img/about/girl-with-hammer@1x.png"
            width="301"
            height="305"
            presentational
          />
        </div>
      </div>
      <div class="container">
        <div class="row mt-5">
          <div class="col-12 col-md-4 mb-5 mb-md-0">
            <FeatureCard
              image-src="/assets/img/workshop/irregular-verbs-illustration@1x.png"
              :title="$t('workshop.features.headlines.irregularVerbs')"
              :button-label="$t('about.ctaBtnText')"
              :link-to="{
                name: 'workshop.irregular-verbs',
                query: ctaButtonQuery,
                params: {
                  packageGroup,
                  languageGroup: 'english'
                }
              }"
            />
          </div>
          <div class="col-12 col-md-4 mb-5 mb-md-0">
            <FeatureCard
              image-src="/assets/img/workshop/inflection-scheme-illustration@1x.png"
              :title="$t('workshop.features.headlines.inflectionTool')"
              :button-label="$t('about.ctaBtnText')"
              :link-to="{
                name: 'workshop.inflection-trainer',
                query: ctaButtonQuery,
                params: {
                  packageGroup,
                  languageGroup: 'german'
                }
              }"
            />
          </div>
          <div class="col-12 col-md-4 mb-5 mb-md-0">
            <FeatureCard
              image-src="/assets/img/workshop/checkbox-illustration@1x.png"
              :title="$t('workshop.features.headlines.orthographicRules')"
              :button-label="$t('about.ctaBtnText')"
              :link-to="{
                name: 'workshop.dsn-spelling-rules',
                query: ctaButtonQuery,
                params: {
                  packageGroup,
                  languageGroup: 'danish'
                }
              }"
            />
          </div>
        </div>
      </div>
    </section>
    <section class="about__section about__section-goodbye">
      <div class="container">
        <div class="row mb-5">
          <div class="col-12">
            <h2 class="about__headline about__headline--secondary">
              {{ $t("about.headlines.goodbye") }}
            </h2>
          </div>
        </div>
        <div class="row mb-5 justify-content-start">
          <div class="col-12 col-md-9 col-lg-7">
            <p class="about__lead">
              {{ $t("about.texts.flexibleSolution") }}
            </p>
          </div>
        </div>
        <div class="col-12 col-md-7 p-0">
          <div class="row no-gutters">
            <div
              class="col-12 col-lg-initial mb-4 mb-lg-0 d-flex d-md-inline-block justify-content-center"
            >
              <TgButton tag="router-link" size="lg" :to="{ name: 'login', query: ctaButtonQuery }">
                {{ $t("about.ctaBtnText") }}
              </TgButton>
            </div>
            <div
              class="col-12 col-lg-initial ml-lg-4 d-flex d-md-inline-block justify-content-center"
            >
              <TgButton
                tag="router-link"
                variant="outline"
                size="lg"
                :to="{ name: 'get-access-today' }"
              >
                {{ $t("about.ctaBtnGetAccessText") }}
              </TgButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>
